import { Link } from "@inertiajs/react";
import Callout from "@/components/help/Callout";
import Screenshot from "@/components/help/Screenshot";
import MembersTable, { InviteMemberForm } from "@/components/schools/MembersTable";
import { MEMBERS, MEMBER_ROLES } from "@/help/fixtures";

export default function ManagingMembers() {
  return (
    <>
      <p>
        Your <strong>Members</strong> page is where you manage everyone who can sign in to your school. Two kinds of people live here: <strong>school admins</strong>, who can do everything, and <strong>teachers</strong>, who can do things in the classrooms they're assigned to. One person can be both.
      </p>

      <Screenshot caption="The members list">
        <MembersTable members={MEMBERS} roles={MEMBER_ROLES.map((r) => r.value)} actionsEnabled={false} />
      </Screenshot>

      <h2>Inviting someone</h2>
      <p>
        Click <strong>Invite someone</strong>, pick whether they're an admin or teacher, type their name and email, and send. They'll get a sign-in email — clicking it lets them in. Until they do, the list shows <strong>Hasn't signed in yet</strong> beside their name.
      </p>

      <Callout variant="note" title="On the free trial">
        <p>
          Until your first campaign is <Link href="/help/articles/paying-for-your-campaign">paid for and approved</Link>, you can add admins but not teachers, and we don't email invitations. New admins sign in from the sign-in page with the email you entered, and the button says <strong>Add them</strong> instead of <strong>Send invitation</strong>.
        </p>
      </Callout>

      <Screenshot caption="The invite form">
        <InviteMemberForm
          createPath="#"
          roles={MEMBER_ROLES.map((r) => r.value)}
          initialName="Mr. Patel"
          initialEmail="mr.patel@lincoln.ca"
        />
      </Screenshot>

      <p>
        Inviting someone as a <strong>teacher</strong> brings them onto your school — but you'll still need to <Link href="/help/articles/inviting-teachers">add them to their classrooms</Link> before they can do anything. Inviting someone as an <strong>admin</strong> gives them full access right away, including Square and donations.
      </p>

      <Callout variant="warning" title="Admins can do everything you can do">
        <p>
          That includes disconnecting Square, editing campaigns, and removing other admins (including you). Save the admin role for people you'd trust with the school bank account.
        </p>
      </Callout>

      <h2>Changing a role or removing someone</h2>
      <p>
        The dropdown in the role column lets you switch someone between admin and teacher — the change is instant. The trash icon removes them from your school. They keep their PagePledge account (they might be a parent, or teach somewhere else), they just can't sign into this school anymore.
      </p>
      <p>
        You can remove yourself, but only if there's at least one other admin. If you're the only admin, promote someone first.
      </p>

      <h2>What's next</h2>
      <ul>
        <li><Link href="/help/articles/inviting-teachers">Adding teachers to classrooms</Link>.</li>
        <li><Link href="/help/articles/school-settings">School settings</Link>.</li>
      </ul>
    </>
  );
}
